import React, { ComponentProps, useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";
import Space from "antd/es/space";
import { useIntl } from "react-intl";

import { Button, LoadingSpinner, Popconfirm } from "@mapmycustomers/ui";

import { ChartMentionsContext } from "@app/component/EntityMentions";
import {
  getNotificationTotalCount,
  getNotificationUnreadCount,
  isNotificationDeleting,
  isNotificationLoading,
  isOnlyMentions,
} from "@app/store/notification";
import {
  deleteNotifications,
  fetchNotifications,
  hideNotifications,
  markNotificationsAsRead,
} from "@app/store/notification/actions";
import { RootState } from "@app/store/rootReducer";
import MmcNotification from "@app/types/MmcNotification";

import NoNotification from "./component/NoNotification";
import Notification from "./component/Notification";
import getGroupHeaderTitle from "./component/util/getGroupHeaderTitle";
import styles from "./NotificationList.module.scss";
import useNotificationChartMentions from "./util/useNotificationChartMentions";

const LOAD_ON_SCROLL_THRESHOLD = 200;

interface NotificationListProps {
  notifications: MmcNotification[];
  onGoToSettings: () => void;
}

const NotificationList = ({
  deleting,
  loading,
  notifications,
  onDeleteNotifications,
  onFetchNotifications,
  onGoToSettings,
  onHideNotifications,
  onlyMentions,
  onMarkNotificationsAsRead,
  total,
  unreadCount,
}: NotificationListProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const chartMentions = useNotificationChartMentions(notifications, onHideNotifications);

  const showDeleteAll = total > 0;
  const showMarkAllRead = unreadCount > 0;

  const handleScroll = useCallback<NonNullable<ComponentProps<"div">["onScroll"]>>(
    (e) => {
      const { clientHeight, scrollHeight, scrollTop } = e.target as HTMLElement;
      if (
        scrollHeight - clientHeight - scrollTop < LOAD_ON_SCROLL_THRESHOLD &&
        !loading &&
        !deleting &&
        total > notifications.length
      ) {
        onFetchNotifications({});
      }
    },
    [onFetchNotifications, loading, deleting, notifications, total]
  );

  const handleDeleteNotifications = useCallback(() => {
    onDeleteNotifications();
  }, [onDeleteNotifications]);

  const handleMarkAllAsRead = useCallback(() => {
    onMarkNotificationsAsRead();
  }, [onMarkNotificationsAsRead]);

  const renderGroupHeader = (index: number) => {
    const title = getGroupHeaderTitle(intl, notifications[index].updatedAt);
    const previousTitle =
      index > 0 ? getGroupHeaderTitle(intl, notifications[index - 1].updatedAt) : "";
    if (title !== previousTitle) {
      return (
        <Row align="middle" className={styles.groupHeader} justify="space-between">
          <Col className={styles.groupHeaderTitle}>{title}</Col>
          <Col>
            <Space size="middle">
              {showDeleteAll && !previousTitle && (
                <Popconfirm
                  danger
                  description={
                    onlyMentions
                      ? intl.formatMessage({
                          id: "navbar.notifications.list.groupHeader.clearAll.confirm.mentionsDescription",
                          defaultMessage:
                            "This will only clear notifications where you were mentioned.",
                          description:
                            "Navbar Notifications - list - group header - clear all - mentions confirmation description",
                        })
                      : intl.formatMessage({
                          id: "navbar.notifications.list.groupHeader.clearAll.confirm.description",
                          defaultMessage: "This will clear all current notifications.",
                          description:
                            "Navbar Notifications - list - group header - clear all - confirmation description",
                        })
                  }
                  okText={intl.formatMessage({
                    id: "navbar.notifications.list.groupHeader.clearAll.confirm.ok",
                    defaultMessage: "Clear",
                    description:
                      "Navbar Notifications - list - group header - clear all - confirmation ok",
                  })}
                  onConfirm={handleDeleteNotifications}
                  title={intl.formatMessage({
                    id: "navbar.notifications.list.groupHeader.clearAll.confirm.title",
                    defaultMessage: "Are you sure?",
                    description:
                      "Navbar Notifications - list - group header - clear all - confirmation title",
                  })}
                >
                  <Button danger type="link">
                    {intl.formatMessage({
                      id: "navbar.notifications.list.groupHeader.clearAll",
                      defaultMessage: "Clear all",
                      description: "Navbar Notifications - list - group header - clear all",
                    })}
                  </Button>
                </Popconfirm>
              )}
              {index === 0 &&
                (showMarkAllRead ? (
                  <Button onClick={handleMarkAllAsRead} type="link">
                    {intl.formatMessage({
                      id: "navbar.notifications.list.groupHeader.markAllRead",
                      defaultMessage: "Mark all read",
                      description: "Navbar Notifications - list - group header - mark all read",
                    })}
                  </Button>
                ) : (
                  <div className={styles.upToDate}>
                    {intl.formatMessage({
                      id: "navbar.notifications.list.groupHeader.upToDate",
                      defaultMessage: "Up to date",
                      description: "Navbar Notifications - list - group header - Up to date",
                    })}
                  </div>
                ))}
            </Space>
          </Col>
        </Row>
      );
    }
  };

  useEffect(() => {
    if (ref.current) {
      ref.current.focus();
    }
  }, [ref]);

  return (
    <div className={styles.container} onScroll={handleScroll} ref={ref} tabIndex={-1}>
      <ChartMentionsContext.Provider value={chartMentions}>
        {!deleting &&
          notifications.length > 0 &&
          notifications.map((notification, i) => (
            <div key={notification.id}>
              {renderGroupHeader(i)}
              <Notification
                notification={notification}
                onGoToSettings={onGoToSettings}
                tabIndex={i + 1}
              />
            </div>
          ))}
      </ChartMentionsContext.Provider>
      {notifications.length === 0 && !loading && <NoNotification onGoToSettings={onGoToSettings} />}
      {(loading || deleting) && <LoadingSpinner />}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  deleting: isNotificationDeleting(state),
  loading: isNotificationLoading(state),
  onlyMentions: isOnlyMentions(state),
  total: getNotificationTotalCount(state),
  unreadCount: getNotificationUnreadCount(state),
});

const mapDispatchToProps = {
  onDeleteNotifications: deleteNotifications.request,
  onFetchNotifications: fetchNotifications.request,
  onHideNotifications: hideNotifications,
  onMarkNotificationsAsRead: markNotificationsAsRead.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(NotificationList);
